<template>
  <BaseModal v-model="isOpen" size="md">
    <template #header>
      {{ $t('event.waitlist_modal_title') }}
    </template>

    <Form @submit="handleSubmit" @invalid-submit="onInvalidSubmit">
      <div class="p-6">
        <p class="mb-4 text-text">{{ $t('event.waitlist_description') }}</p>

        <div class="mb-4">
          <InputText
            id="waitlist-email"
            name="email"
            type="email"
            :rules="rules.email"
            v-model="form.email"
            :placeholder="$t('checkout.type') + ' ' + $t('checkout.email_placeholder')"
            required
          />
        </div>

        <BaseButton
          class="w-full relative group"
          color="primary"
          size="lg"
          type="submit"
          :disabled="isSubmitting"
        >
          <span class="w-full flex items-center justify-center">
            {{ $t('event.subscribe_to_waitlist') }}
            <ArrowRightIcon
              class="w-4 h-4 ml-2 fill-current group-hover:animate-pulse-right"
            />
          </span>
        </BaseButton>

        <div v-if="errorMessage" class="text-sm block pt-2 text-error">
          {{ errorMessage }}
        </div>

        <div v-if="successMessage" class="text-sm block pt-2 text-success">
          {{ successMessage }}
        </div>
      </div>
    </Form>
  </BaseModal>
</template>

<script setup lang="ts">
import { Form, type InvalidSubmissionHandler, type SubmissionHandler } from 'vee-validate';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import { subscribeToWaitlist } from '@/api/waitlist';

import ArrowRightIcon from '../icons/ArrowRightIcon.vue';
import BaseButton from '../ui/BaseButton.vue';
import BaseModal from '../ui/BaseModal.vue';
import InputText from '../ui/inputs/InputText.vue';

const props = defineProps<{
  modelValue: boolean;
  event: {
    id: number | string;
    waitlists: number[];
  };
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const { t } = useI18n();

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const form = reactive({ email: '' });

const rules = {
  email: yup
    .string()
    .email(t('validation.email'))
    .required(t('validation.required')),
};

const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);
const isSubmitting = ref(false);

// Reset state when modal opens
watch(() => props.modelValue, (open) => {
  if (open) {
    form.email = '';
    errorMessage.value = null;
    successMessage.value = null;
  }
});

const handleSubmit: SubmissionHandler = async (values) => {
  const { email } = values as { email: string };

  isSubmitting.value = true;
  errorMessage.value = null;
  successMessage.value = null;

  try {
    const result = await subscribeToWaitlist(
      props.event.waitlists[0],
      email,
      props.event.id,
    );

    if (result.status === 'ok') {
      successMessage.value = t('event.waitlist_success');
    } else if (result.status === 'invalidemail') {
      errorMessage.value = t('event.waitlist_error_invalid_email');
    } else {
      errorMessage.value = t('event.waitlist_error_generic');
    }
  } catch {
    errorMessage.value = t('event.waitlist_error_generic');
  } finally {
    isSubmitting.value = false;
  }
};

const onInvalidSubmit: InvalidSubmissionHandler = () => {
  // vee-validate handles field-level errors
};
</script>
